import React, {
  useCallback,
  useMemo,
  Children,
  useState,
  useRef,
  useEffect,
  type ReactElement,
} from 'react';

import { TranslationContext, type I18nProvider } from './TranslationContext';

interface TranslationState {
  locale: string;
  i18nProvider: I18nProvider | null;
}

function useSafeSetState<T>(initialState: T): [T, (state: T) => void] {
  const [state, setState] = useState<T>(initialState);

  const mountedRef = useRef(false);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);
  const safeSetState = useCallback(
    (args: T) => mountedRef.current && setState(args),
    [mountedRef, setState],
  );
  return [state, safeSetState];
}

/**
 * Creates a translation context, available to its children
 *
 * @example
 *     const MyApp = () => (
 *         <Provider store={store}>
 *             <TranslationProvider i18nProvider={i18nProvider}>
 *                 <!-- Child components go here -->
 *             </TranslationProvider>
 *         </Provider>
 *     );
 */

interface TranslationProviderProps {
  i18nProvider?: I18nProvider | null;
  children: ReactElement;
}

const TranslationProvider: React.FC<TranslationProviderProps> = ({
  i18nProvider = null,
  children,
}) => {
  const [state, setState] = useSafeSetState<TranslationState>({
    locale: i18nProvider ? i18nProvider.getLocale() : 'en',
    i18nProvider,
  });

  const setLocale = useCallback(
    (newLocale: string) =>
      setState({
        locale: newLocale,
        i18nProvider,
      }),
    [i18nProvider, setState],
  );

  // Allow locale modification by including setLocale in the context
  // This can't be done in the initial state because setState doesn't exist yet
  const value = useMemo(
    () => ({
      ...state,
      setLocale,
    }),
    [setLocale, state],
  );

  return (
    <TranslationContext.Provider value={value}>
      {Children.only(children)}
    </TranslationContext.Provider>
  );
};

export default TranslationProvider;
